import { useEffect, useState } from "react";
import { useAuthStore } from "@/store/authStore";
import { deckService, cardProgressService } from "@/services/firestore";
import { FlashcardProgress, Deck } from "@/types";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription
} from "@/components/ui/card";
import { 
  TrendingUp, 
  Calendar, 
  CheckCircle2, 
  Zap,
  BarChart3,
  PieChart,
  Target,
  ArrowUpRight
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Helmet } from "react-helmet-async";

export default function AnalyticsPage() {
  const { user, loading: authLoading } = useAuthStore();
  const [progress, setProgress] = useState<FlashcardProgress[]>([]);
  const [decks, setDecks] = useState<Deck[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      const fetchData = async () => {
        try {
          const [allProgress, userDecks] = await Promise.all([
            cardProgressService.getAllProgressForUser(user.uid),
            deckService.getDecks(user.uid)
          ]);
          setProgress(allProgress);
          setDecks(userDecks);
        } catch (error) {
          console.error("Error fetching analytics data:", error);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }
  }, [user]);

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Calculate statistics
  const totalStudied = progress.length;
  const masteredCards = progress.filter(p => p.easeFactor >= 2.8 && p.interval > 30).length;
  const retentionRate = totalStudied > 0 ? Math.round((masteredCards / totalStudied) * 100) : 0;
  
  // Group by deck for mastery breakdown
  const deckStats = decks.map(deck => {
    const deckProgress = progress.filter(p => p.deckId === deck.id);
    const deckMastered = deckProgress.filter(p => p.easeFactor >= 2.8 && p.interval > 30).length;
    const totalDeckCards = deck.cardCount || 0;
    const mastery = totalDeckCards > 0 ? Math.round((deckMastered / totalDeckCards) * 100) : 0;
    
    return {
      ...deck,
      mastery,
      studiedCount: deckProgress.length
    };
  }).sort((a, b) => b.mastery - a.mastery);

  return (
    <div className="space-y-12 max-w-6xl mx-auto py-8 md:py-12 px-4 md:px-6">
      <Helmet>
        <title>Analytics | Flashcard Platform</title>
      </Helmet>

      <div className="space-y-2">
        <h1 className="text-4xl font-extrabold tracking-tight lg:text-5xl">Analytics</h1>
        <p className="text-xl text-muted-foreground">
          Track your progress, identify weak spots, and celebrate your mastery.
        </p>
      </div>

      {/* Hero Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-none bg-primary text-primary-foreground shadow-xl shadow-primary/20 overflow-hidden relative text-white">
          <Zap className="absolute -right-4 -bottom-4 h-32 w-32 opacity-10 rotate-12" />
          <CardContent className="p-6">
            <p className="text-sm font-medium opacity-80 uppercase tracking-wider mb-1">Knowledge Retention</p>
            <h3 className="text-4xl font-bold">{retentionRate}%</h3>
            <div className="flex items-center gap-1 mt-4 text-xs font-medium bg-white/20 w-fit px-2 py-1 rounded-full">
              <ArrowUpRight className="h-3 w-3" /> +2% from last week
            </div>
          </CardContent>
        </Card>

        {[
          { icon: Target, label: "Mastered Cards", value: masteredCards, color: "text-green-500", bg: "bg-green-500/10" },
          { icon: TrendingUp, label: "Total Reviews", value: progress.reduce((acc, curr) => acc + curr.repetition, 0), color: "text-blue-500", bg: "bg-blue-500/10" },
          { icon: Calendar, label: "Current Streak", value: "3 days", color: "text-orange-500", bg: "bg-orange-500/10" },
        ].map((stat, i) => (
          <Card key={i} className="border-none bg-muted/30">
            <CardHeader className="p-6 pb-2">
               <div className={`${stat.bg} ${stat.color} p-2.5 rounded-xl w-fit mb-4`}>
                <stat.icon className="h-5 w-5" />
              </div>
              <CardDescription className="text-xs uppercase tracking-widest font-bold">{stat.label}</CardDescription>
              <CardTitle className="text-3xl font-bold">{stat.value}</CardTitle>
            </CardHeader>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Mastery by Deck */}
        <Card className="lg:col-span-2 shadow-sm border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" /> Mastery by Subject
            </CardTitle>
            <CardDescription>How well you know each of your decks.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            {deckStats.length > 0 ? deckStats.map((deck) => (
              <div key={deck.id} className="space-y-3">
                <div className="flex justify-between items-end">
                  <div>
                    <p className="font-bold text-lg">{deck.title}</p>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">{deck.studiedCount}/{deck.cardCount || 0} cards studied</p>
                  </div>
                  <Badge variant={deck.mastery > 80 ? "default" : "secondary"} className={cn(deck.mastery > 80 && "bg-green-500 hover:bg-green-600")}>
                    {deck.mastery}% Mastered
                  </Badge>
                </div>
                <div className="h-3 w-full bg-muted rounded-full overflow-hidden">
                  <div 
                    className={cn(
                      "h-full transition-all duration-1000",
                      deck.mastery > 80 ? "bg-green-500" : deck.mastery > 50 ? "bg-blue-500" : "bg-primary"
                    )}
                    style={{ width: `${deck.mastery}%` }}
                  />
                </div>
              </div>
            )) : (
              <div className="text-center py-10 text-muted-foreground">
                Start studying decks to see your mastery metrics here.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Learning Activity */}
        <Card className="shadow-sm border-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-primary" /> Learning Mix
            </CardTitle>
            <CardDescription>Breakdown of card difficulty.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-8 pt-4">
            <div className="flex flex-col gap-6">
               {[
                 { label: "New", count: progress.filter(p => p.repetition === 1).length, color: "bg-blue-500" },
                 { label: "Learning", count: progress.filter(p => p.repetition > 1 && p.interval < 7).length, color: "bg-orange-500" },
                 { label: "Review", count: progress.filter(p => p.interval >= 7).length, color: "bg-green-500" },
                 { label: "Lapsed", count: progress.filter(p => p.easeFactor < 2.0).length, color: "bg-red-500" },
               ].map((item, i) => (
                 <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn("h-3 w-3 rounded-full", item.color)} />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <span className="font-bold">{item.count} cards</span>
                 </div>
               ))}
            </div>

            <div className="pt-6 border-t">
               <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                 <CheckCircle2 className="h-4 w-4 text-green-500" />
                 <span>Overall Learning Capacity</span>
               </div>
               <div className="text-4xl font-extrabold">{Math.round(totalStudied * 1.5)}h</div>
               <p className="text-xs text-muted-foreground mt-1">Estimated total study time based on review sessions.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
